import {useContext, useState} from 'react';
import {Linking, ScrollView, StyleSheet, View} from 'react-native';
import {TouchableOpacity} from 'react-native-gesture-handler';
import {
  Button,
  Caption,
  DataTable,
  IconButton,
  Title,
} from 'react-native-paper';
import Icon from 'react-native-vector-icons/MaterialCommunityIcons';
import {useMutation, useQueryClient} from 'react-query';
import firestore from '@react-native-firebase/firestore';

import {UserContext} from '../App';
import {useAppSettings} from '../components/AppSettings';
import {
  Person,
  PersonRoles,
  useAuthPersonInfo,
  usePeople,
} from '../hooks/usePersonInfo';
import {PersonInfoContext} from './Stack';
import {PersonEditor} from '../components/FormikStoreEditors/PersonEditor';

export function PersonComponent(props: {
  person: Person;
  readOnly?: boolean;
}): JSX.Element {
  const authuser = useAuthPersonInfo();
  const appSettings = useAppSettings();
  const queryClient = useQueryClient();

  const [editDialogVisible, setEditDialogVisible] = useState(false);
  const [currentFirestoreObject, setCurrentFirestoreObject] = useState<
    Person | undefined
  >();

  const collectionName = 'people';
  const handleDeleteFirestoreObject = useMutation(
    async (firestoreObject: Person) => {
      console.log('should delete: ' + firestoreObject.ref.path);

      // TODO FIXME delete the auth user here

      return firestore()
        .collection(collectionName)
        .doc(firestoreObject.ref.id)
        .delete();
    },
    {
      onSettled: () => {
        console.log('deleting succeeded');
        return queryClient.invalidateQueries(collectionName);
      },
    },
  );

  const handleEditFirestoreObject = (firestoreObject?: Person) => {
    // console.log('open for edit or new object: ' + firestoreObject?.ref.path);
    setCurrentFirestoreObject(firestoreObject);
    setEditDialogVisible(true);
  };

  return (
    <View style={styles.row} key={props.person.personId}>
      <View style={styles.horizontalSpaced}>
        <Caption>{`Contact: ${props.person.firstName} ${props.person.lastName}`}</Caption>
        {props.person.email && (
          <TouchableOpacity
            onPress={() => Linking.openURL(`mailto:${props.person.email}`)}
            style={styles.touchableRow}>
            <Icon
              name="email"
              color={appSettings.currentTheme.colors.primary}
            />
            <Caption style={{color: appSettings.currentTheme.colors.primary}}>
              {'  '}
              {props.person.email}
            </Caption>
          </TouchableOpacity>
        )}
        {props.person.phoneNumber1 && (
          <TouchableOpacity
            onPress={() => Linking.openURL(`tel:${props.person.phoneNumber1}`)}
            style={styles.touchableRow}>
            <Icon
              name="phone-outgoing"
              color={appSettings.currentTheme.colors.primary}
            />
            <Caption style={{color: appSettings.currentTheme.colors.primary}}>
              {'  '}
              {props.person.phoneNumber1}
            </Caption>
          </TouchableOpacity>
        )}
        {(authuser?.isAdmin || authuser?.isGuestRelations) && (
          <Caption> Hubspot {props.person.hubspotId}</Caption>
        )}
        <View style={styles.horizontalEnd}>
          {!props.readOnly &&
            (authuser?.isAdmin || authuser?.isGuestRelations) && (
              <>
                <Caption>Person Actions: </Caption>
                <IconButton
                  icon="pencil"
                  size={18}
                  color={appSettings.currentTheme.colors.primary}
                  onPress={() => handleEditFirestoreObject(props.person)}
                />
                <PersonEditor
                  dialogVisible={editDialogVisible}
                  setDialogVisible={(visible: boolean) => {
                    setEditDialogVisible(visible);
                    if (!visible) {
                      setCurrentFirestoreObject(undefined);
                    }
                  }}
                  person={currentFirestoreObject}
                />
              </>
            )}
          {!props.readOnly && authuser?.isAdmin && (
            <IconButton
              icon="delete"
              size={18}
              color={appSettings.currentTheme.colors.primary}
              onPress={() => handleDeleteFirestoreObject.mutate(props.person)}
            />
          )}
        </View>
      </View>
    </View>
  );
}

function People() {
  const user = useContext(UserContext);
  const people = usePeople(Object.values(PersonRoles));
  const personInfo = useContext(PersonInfoContext);
  const queryClient = useQueryClient();
  const appSettings = useAppSettings();

  const [editDialogVisible, setEditDialogVisible] = useState(false);
  const [currentFirestoreObject, setCurrentFirestoreObject] = useState<
    Person | undefined
  >();

  const collectionName = 'people';
  const handleDeleteFirestoreObject = useMutation(
    (firestoreObject: Person) => {
      console.log('should delete: ' + firestoreObject.ref.path);
      return firestore()
        .collection(collectionName)
        .doc(firestoreObject.ref.id)
        .delete();
    },
    {
      onSettled: () => {
        console.log('deleting succeeded');
        return queryClient.invalidateQueries(collectionName);
      },
    },
  );

  const handleEditFirestoreObject = (firestoreObject?: Person) => {
    // console.log('open for edit or new object: ' + firestoreObject?.ref.path);
    setCurrentFirestoreObject(firestoreObject);
    setEditDialogVisible(true);
  };

  if (!user) {
    return null;
  }

  return (
    <>
      <View style={styles.horizontalCentered}>
        <Title style={styles.title}>People</Title>
        {(personInfo?.filteredPersonInfo?.authPerson?.isAdmin ||
          personInfo?.filteredPersonInfo?.authPerson?.isGuestRelations) && (
          <Button mode="outlined" onPress={() => handleEditFirestoreObject()}>
            Create Person
          </Button>
        )}
      </View>
      <ScrollView
        persistentScrollbar={true}
        contentContainerStyle={styles.content}
        style={[styles.content]}>
        <DataTable>
          {!people && (
            <DataTable.Row key="row">
              <DataTable.Header>
                <DataTable.Title>loading...</DataTable.Title>
              </DataTable.Header>
            </DataTable.Row>
          )}
          {people && (
            <DataTable.Header key="header" style={styles.shortHeader}>
              <DataTable.Title style={styles.widerCell}>First</DataTable.Title>
              <DataTable.Title style={[styles.borderCell, styles.widerCell]}>
                Last
              </DataTable.Title>
              <DataTable.Title style={[styles.borderCell, styles.smallerCell]}>
                Roles
              </DataTable.Title>
              <DataTable.Title style={[styles.borderCell, styles.widerCell]}>
                Email
              </DataTable.Title>
              <DataTable.Title style={[styles.borderCell, styles.smallerCell]}>
                Hubspot
              </DataTable.Title>
              {(personInfo?.filteredPersonInfo?.authPerson?.isAdmin ||
                personInfo?.filteredPersonInfo?.authPerson
                  ?.isGuestRelations) && (
                <DataTable.Title
                  style={[styles.borderCell, styles.smallerCell]}>
                  Actions
                </DataTable.Title>
              )}
            </DataTable.Header>
          )}
          {people && people.length === 0 && (
            <DataTable.Row key="notfound">
              <DataTable.Cell>No people found</DataTable.Cell>
            </DataTable.Row>
          )}
          {people &&
            people.length > 0 &&
            people.map(person => (
              <DataTable.Row key={person.personId + '' + person.authUid}>
                <DataTable.Cell style={styles.widerCell}>
                  {person.firstName}
                </DataTable.Cell>
                <DataTable.Cell style={[styles.borderCell, styles.widerCell]}>
                  {person.lastName}
                </DataTable.Cell>
                <DataTable.Cell style={[styles.borderCell, styles.smallerCell]}>
                  {person.roles?.join(', ')}
                </DataTable.Cell>
                <DataTable.Cell style={[styles.borderCell, styles.widerCell]}>
                  {person.email}
                </DataTable.Cell>
                <DataTable.Cell style={[styles.borderCell, styles.smallerCell]}>
                  {person.hubspotId}
                </DataTable.Cell>
                {(personInfo?.filteredPersonInfo?.authPerson?.isAdmin ||
                  personInfo?.filteredPersonInfo?.authPerson
                    ?.isGuestRelations) && (
                  <DataTable.Cell
                    style={[styles.borderCell, styles.smallerCell]}>
                    <>
                      {(personInfo?.filteredPersonInfo?.authPerson?.isAdmin ||
                        personInfo?.filteredPersonInfo?.authPerson
                          ?.isGuestRelations) && (
                        <IconButton
                          icon="pencil"
                          size={18}
                          color={appSettings.currentTheme.colors.primary}
                          onPress={() => handleEditFirestoreObject(person)}
                        />
                      )}
                      {personInfo?.filteredPersonInfo?.authPerson?.isAdmin && (
                        <IconButton
                          icon="delete"
                          size={18}
                          color={appSettings.currentTheme.colors.primary}
                          onPress={() =>
                            handleDeleteFirestoreObject.mutate(person)
                          }
                        />
                      )}
                    </>
                  </DataTable.Cell>
                )}
              </DataTable.Row>
            ))}
        </DataTable>
      </ScrollView>
      <PersonEditor
        dialogVisible={editDialogVisible}
        setDialogVisible={(visible: boolean) => {
          setEditDialogVisible(visible);
          if (!visible) {
            setCurrentFirestoreObject(undefined);
          }
        }}
        person={currentFirestoreObject}
      />
    </>
  );
}

const styles = StyleSheet.create({
  touchableRow: {
    paddingHorizontal: 5,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
  },
  row: {
    flexDirection: 'row',
  },
  widerCell: {
    minWidth: 150,
    width: 130,
    justifyContent: 'center',
    alignItems: 'center',
  },
  smallerCell: {
    width: 90,
    maxWidth: 90,
  },
  borderCell: {
    borderStartWidth: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  container: {
    flex: 1,
    // height: '100%',
    // position: 'relative',
  },
  content: {
    flex: 1,
    flexGrow: 1,
    paddingHorizontal: 20,
  },
  center: {
    alignItems: 'center',
  },
  shortHeader: {
    height: 25,
  },
  title: {
    padding: 10,
    paddingHorizontal: 20,
  },
  horizontalCentered: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    alignContent: 'center',
    paddingHorizontal: 10,
  },
  horizontalSpaced: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  horizontalEnd: {
    flex: 1,
    flexDirection: 'row',
    paddingHorizontal: 10,
    justifyContent: 'flex-end',
    alignSelf: 'flex-end',
    alignItems: 'center',
  },
});

export default People;
